import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { hooks as h } from "@/hooks";
import { components as c } from "@/components";
import Stepper from "@/components/common/Stepper";
import { InviteMethodButtons } from "@/features/invite/components/InviteMethodButtons";
import SelectRole from "@/features/invite/components/SelectRole";
import { parseOrganisationField } from "@/features/invite/utils/parseOrganisationField";
import { toTeamInviteSearch } from "@/features/invite/utils/teamInviteSearch";
import { getTeamInviteSteps } from "@/features/invite/utils/teamInviteSteps";
import { useInviteCopy } from "@/features/i18n/inviteCopy";
import { useProfileCopy } from "@/features/i18n/profileCopy";
import SelectFromMyOrganisations from "@/features/organisations/components/SelectFromMyOrganisations";
import { useAuth } from "@/utils/auth";

export const Route = createFileRoute("/_app/team/invite/")({
    component: TeamInviteHubPage,
});

interface TeamInviteHubValues {
    role: string;
    organisation: string;
}

function TeamInviteHubPage() {
    const navigate = useNavigate();
    const auth = useAuth();
    const copy = useProfileCopy();
    const inviteCopy = useInviteCopy();
    const search = Route.useSearch();
    const includeElevatedRoles = auth.roles.isAdmin || auth.isSuperUser;

    h.useNav([
        { label: copy.myTeam, route: { to: "/team" } },
        { label: inviteCopy.invitePageTitle, route: { to: "/team/invite", search } },
    ]);

    const form = useForm<TeamInviteHubValues>({
        defaultValues: {
            role: search.role ?? "",
            organisation: search.organisation_id
                ? JSON.stringify({
                      organisation_id: search.organisation_id,
                      organisation_name: search.organisation_name ?? "",
                  })
                : "",
        },
    });

    const role = form.watch("role");
    const organisation = parseOrganisationField(form.watch("organisation"));
    const canContinue = Boolean(role && organisation);

    const goTo = (to: "/team/invite/email" | "/team/invite/qr") => {
        if (!role || !organisation) return;
        void navigate({
            to,
            search: toTeamInviteSearch(role, organisation),
        });
    };

    return (
        <>
            <c.cc.Title words={inviteCopy.invitePageTitle} />
            <Stepper steps={getTeamInviteSteps("setup", inviteCopy, search)} />
            <c.ui.Form {...form}>
                <form className="space-y-6" onSubmit={(event) => event.preventDefault()}>
                    <SelectRole required includeElevatedRoles={includeElevatedRoles} />
                    <SelectFromMyOrganisations required />
                    <InviteMethodButtons
                        disabled={!canContinue}
                        emailTestId="team-invite-by-email"
                        qrTestId="team-invite-share-qr"
                        onInviteByEmail={() => goTo("/team/invite/email")}
                        onShareQr={() => goTo("/team/invite/qr")}
                    />
                </form>
            </c.ui.Form>
        </>
    );
}
